import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Configuration options for useCachedLoader
 */
interface UseCachedLoaderOptions<T> {
  /** Initial value before first load */
  initialValue: T
  /** Paths where loading should be skipped (e.g., '/login') */
  skipPaths?: string[]
  /** Whether to skip loading entirely */
  skip?: boolean
}

/**
 * Return type for useCachedLoader
 */
interface UseCachedLoaderReturn<T> {
  /** Current data value */
  data: T
  /** Whether initial load is in progress */
  loading: boolean
  /** Manually trigger a reload */
  reload: () => Promise<void>
  /** Update data locally (for optimistic updates) */
  setData: React.Dispatch<React.SetStateAction<T>>
}

/**
 * A utility hook that handles cached data loading with duplicate prevention.
 *
 * This hook solves a common problem: when using React contexts that trigger
 * re-renders (like Supabase auth), data loading effects can fire multiple times
 * even when the actual key (like userId) hasn't changed.
 *
 * Features:
 * - Only loads once per unique key value
 * - Resets data when key becomes null (e.g., user logs out)
 * - Skips showing loading spinner when data already exists (smoother UX)
 * - Supports path-based skip conditions
 *
 * @example
 * ```tsx
 * const { data: profile, loading, setData } = useCachedLoader(
 *   user?.id ?? null,
 *   async () => {
 *     const { data, error } = await ProfileService.get()
 *     return { data, error }
 *   },
 *   { initialValue: null, skipPaths: ['/login'] }
 * )
 * ```
 */
export function useCachedLoader<T>(
  /** The cache key - typically userId or mindmapId. Loading occurs when this changes. */
  cacheKey: string | null,
  /** Async function that fetches the data. Should return { data, error? } */
  fetchFn: () => Promise<{ data: T | null; error?: unknown }>,
  /** Configuration options */
  options: UseCachedLoaderOptions<T>
): UseCachedLoaderReturn<T> {
  const { initialValue, skipPaths = [], skip = false } = options

  const [data, setData] = useState<T>(initialValue)
  const [loading, setLoading] = useState(true)

  // Track what key we've loaded to prevent duplicate loads
  const loadedKeyRef = useRef<string | null>(null)
  // Track if we have data (to avoid showing loading when refreshing)
  const hasDataRef = useRef(false)

  // Helper to check current pathname
  const getCurrentPathname = useCallback(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname
    }
    return ''
  }, [])

  // The actual load function
  const load = useCallback(async () => {
    try {
      // Only show loading if we don't already have data
      if (!hasDataRef.current) {
        setLoading(true)
      }

      const { data: result, error } = await fetchFn()

      if (error) {
        setData(initialValue)
        hasDataRef.current = false
        return
      }

      setData(result as T)
      hasDataRef.current = true
    } catch {
      setData(initialValue)
      hasDataRef.current = false
    } finally {
      setLoading(false)
    }
  }, [fetchFn, initialValue])

  // Effect that manages loading based on key changes
  useEffect(() => {
    const currentPath = getCurrentPathname()
    const shouldSkipForPath = skipPaths.some((path) => currentPath === path)

    if (skip || shouldSkipForPath) {
      setLoading(false)
      return
    }

    if (cacheKey) {
      // Only load if the key actually changed
      if (loadedKeyRef.current !== cacheKey) {
        loadedKeyRef.current = cacheKey
        void load()
      }
    } else {
      // Reset when key becomes null (e.g., user logs out)
      loadedKeyRef.current = null
      hasDataRef.current = false
      setData(initialValue)
      setLoading(false)
    }
  }, [cacheKey, load, getCurrentPathname, skipPaths, skip, initialValue])

  // Manual reload function (forces reload regardless of cache)
  const reload = useCallback(async () => {
    if (cacheKey) {
      await load()
    }
  }, [cacheKey, load])

  return {
    data,
    loading,
    reload,
    setData,
  }
}
